import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { DayPicker, getDefaultClassNames } from "@daypicker/react";
import { enGB, nl } from "@daypicker/react/locale";
import "@daypicker/react/style.css";

/**
 * Het datumveld van het systeem: een veld waarin je dd-mm-jjjj typt, met een kalender die eronder
 * openklapt. Hij vervangt de datumkiezer van de browser — die zag er in elke browser anders uit, kon
 * niet worden vormgegeven, en toonde amerikaanse maanden.
 *
 * <p>De waarde blijft {@code jjjj-mm-dd}, en {@code onChange} levert net als een gewoon invoerveld een
 * gebeurtenis met {@code target.value}. Daardoor kan elk bestaand {@code type="date"}-veld hierop over
 * zonder dat de aanroeper iets hoeft te veranderen.
 *
 * <p>De kalender staat in een portal en wordt op het scherm gepositioneerd: een veld in een kaart met
 * {@code overflow: hidden} of in een venster zou hem anders afknippen. Past hij er onder niet, dan
 * klapt hij boven het veld open.
 *
 * <p>De taal volgt {@code <html lang>}, zodat de maandnamen meelopen met de taal van het systeem
 * zonder dat dit pakket iets van de vertalingen hoeft te weten.
 */
export interface DateFieldTeksten {
  kalender: string;
  vandaag: string;
  wissen: string;
  ongeldig: string;
}

const NL: DateFieldTeksten = {
  kalender: "Kalender openen",
  vandaag: "Vandaag",
  wissen: "Wissen",
  ongeldig: "Vul een datum in als dd-mm-jjjj.",
};

const EN: DateFieldTeksten = {
  kalender: "Open calendar",
  vandaag: "Today",
  wissen: "Clear",
  ongeldig: "Enter a date as dd-mm-yyyy.",
};

export interface DateFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "min" | "max"> {
  /** Leeg laten als het veld al een eigen label buiten zich heeft; geef dan wel `aria-label` mee. */
  label?: string;
  /** jjjj-mm-dd, of leeg. */
  value?: string;
  /** Krijgt een gebeurtenis waarvan `target.value` de datum is als jjjj-mm-dd, of leeg. */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** jjjj-mm-dd: eerder kan niet worden gekozen. */
  min?: string;
  /** jjjj-mm-dd: later kan niet worden gekozen. */
  max?: string;
  /**
   * Losse dagen die niet te kiezen zijn, als vraag per dag: krijgt jjjj-mm-dd en antwoordt of die dag
   * dicht is. Waar `min` en `max` een aaneengesloten bereik afbakenen, gaat dit over dagen verspreid
   * door de kalender — een pension dat op zondag geen dieren meegeeft, bijvoorbeeld. Zulke dagen staan
   * grijs en zijn niet aan te klikken.
   */
  dagDicht?: (iso: string) => boolean;
  /** Een melding onder het veld. */
  error?: ReactNode;
  /** "nl" of "en"; standaard de taal van de pagina. */
  locale?: string;
  /** De knoppen Vandaag en Wissen onder de kalender. */
  showFooterActions?: boolean;
  teksten?: Partial<DateFieldTeksten>;
}

/** De kalender heeft ruimte nodig; past hij er onder niet, dan gaat hij erboven. */
const KALENDER_HOOGTE = 380;
const KALENDER_BREEDTE = 320;

const isoVorm = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Een datum zonder tijdzone: een kalenderdag is geen moment. */
export function datumVanIso(iso: string | undefined | null): Date | undefined {
  const m = typeof iso === "string" ? isoVorm.exec(iso.slice(0, 10)) : null;
  if (!m) return undefined;
  const datum = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return datum.getFullYear() === Number(m[1]) && datum.getMonth() === Number(m[2]) - 1 ? datum : undefined;
}

export function isoVanDatum(datum: Date | undefined): string {
  if (!datum) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${datum.getFullYear()}-${p(datum.getMonth() + 1)}-${p(datum.getDate())}`;
}

/** dd-mm-jjjj, de schrijfwijze die iedereen hier typt. */
function getoond(datum: Date | undefined): string {
  if (!datum) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(datum.getDate())}-${p(datum.getMonth() + 1)}-${datum.getFullYear()}`;
}

/**
 * Wat er getypt is. Scheidingstekens mogen streepjes, punten, schuine strepen of niets zijn, en het
 * jaartal mag twee cijfers zijn — 24 wordt 2024. Acht cijfers achter elkaar (14052018) mag ook, want
 * zo typt een balie een datum uit een dossier over. Begint het met vier cijfers, dan is het jjjj-mm-dd:
 * zo komt een datum uit een ander scherm of uit een plakactie ook goed binnen.
 */
export function leesGetypteDatum(invoer: string): Date | undefined {
  const schoon = invoer.trim();
  if (schoon === "") return undefined;
  const cijfers = schoon.replace(/[^\d]/g, "");
  let d: number;
  let m: number;
  let j: number;
  const delen = schoon.split(/[^\d]+/).filter(Boolean);
  if (delen.length === 3 && delen[0].length === 4) {
    [j, m, d] = delen.map(Number);
  } else if (delen.length === 3) {
    [d, m, j] = delen.map(Number);
  } else if (cijfers.length === 8) {
    d = Number(cijfers.slice(0, 2));
    m = Number(cijfers.slice(2, 4));
    j = Number(cijfers.slice(4));
  } else if (cijfers.length === 6) {
    d = Number(cijfers.slice(0, 2));
    m = Number(cijfers.slice(2, 4));
    j = Number(cijfers.slice(4));
  } else {
    return undefined;
  }
  if (j < 100) {
    // Een jaartal van twee cijfers: 70 t/m 99 is vorige eeuw, de rest deze.
    j += j >= 70 ? 1900 : 2000;
  }
  const datum = new Date(j, m - 1, d);
  return datum.getFullYear() === j && datum.getMonth() === m - 1 && datum.getDate() === d ? datum : undefined;
}

/**
 * De gebeurtenis die de aanroeper krijgt. Hij leest er `target.value` uit, net als bij een gewoon
 * invoerveld; de echte invoer toont dd-mm-jjjj, dus die kan hem niet zelf leveren.
 */
function wijziging(iso: string, invoer: HTMLInputElement | null): ChangeEvent<HTMLInputElement> {
  const doel = { value: iso, name: invoer?.name ?? "", id: invoer?.id ?? "", type: "date" };
  return {
    target: doel,
    currentTarget: doel,
    type: "change",
    bubbles: true,
    preventDefault: () => {},
    stopPropagation: () => {},
  } as unknown as ChangeEvent<HTMLInputElement>;
}

function taalVan(locale: string | undefined): string {
  if (locale) return locale;
  const paginaTaal = typeof document === "undefined" ? "" : document.documentElement.lang;
  return paginaTaal || "nl";
}

export function DateField({
  label,
  value,
  onChange,
  min,
  max,
  dagDicht,
  name,
  id,
  required,
  disabled,
  readOnly,
  placeholder = "dd-mm-jjjj",
  error,
  className,
  locale,
  showFooterActions = true,
  teksten,
  ...rest
}: DateFieldProps) {
  const taal = taalVan(locale);
  const woorden = { ...(taal.startsWith("en") ? EN : NL), ...teksten };
  const eigenId = useId();
  const veldId = id ?? eigenId;
  const meldingId = `${veldId}-melding`;

  const gekozen = datumVanIso(value);
  const [getypt, setGetypt] = useState(() => getoond(gekozen));
  const [open, setOpen] = useState(false);
  const [ongeldig, setOngeldig] = useState(false);
  const [maand, setMaand] = useState<Date>(gekozen ?? new Date());
  const [plek, setPlek] = useState<CSSProperties>({});

  const wortel = useRef<HTMLDivElement>(null);
  const invoer = useRef<HTMLInputElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const standaardKlassen = getDefaultClassNames();

  // De waarde van buiten wint, behalve als wat er staat al hetzelfde betekent: anders springt de cursor
  // naar het eind zodra de aanroeper de waarde terugstuurt die je net typte.
  useEffect(() => {
    const aanHetTypen = typeof document !== "undefined" && document.activeElement === invoer.current;
    if (aanHetTypen && isoVanDatum(leesGetypteDatum(getypt)) === (value ?? "")) return;
    setGetypt(getoond(gekozen));
    setOngeldig(false);
    if (gekozen) setMaand(gekozen);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const plaats = useCallback(() => {
    const vak = wortel.current?.getBoundingClientRect();
    if (!vak) return;
    const onder = window.innerHeight - vak.bottom;
    const boven = vak.top;
    const omhoog = onder < KALENDER_HOOGTE && boven > onder;
    const links = Math.min(Math.max(8, vak.left), Math.max(8, window.innerWidth - KALENDER_BREEDTE - 8));
    setPlek(
      omhoog
        ? { left: links, bottom: Math.round(window.innerHeight - vak.top + 6) }
        : { left: links, top: Math.round(vak.bottom + 6) },
    );
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    plaats();
    window.addEventListener("resize", plaats);
    // Scrollt de pagina onder de kalender weg, dan schuift hij mee in plaats van te blijven zweven.
    window.addEventListener("scroll", plaats, true);
    return () => {
      window.removeEventListener("resize", plaats);
      window.removeEventListener("scroll", plaats, true);
    };
  }, [open, plaats]);

  useEffect(() => {
    if (!open) return;
    const buiten = (e: MouseEvent) => {
      const doel = e.target;
      if (!(doel instanceof Node)) return;
      if (wortel.current?.contains(doel) || popup.current?.contains(doel)) return;
      setOpen(false);
    };
    const toets = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        invoer.current?.focus();
      }
    };
    document.addEventListener("mousedown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("mousedown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [open]);

  const grens = { vanaf: datumVanIso(min), tot: datumVanIso(max) };
  const buitenGrenzen = (datum: Date) =>
    (grens.vanaf != null && datum < grens.vanaf) || (grens.tot != null && datum > grens.tot);

  /**
   * Of de aanroeper deze dag dicht noemt. Alleen de kalender trekt zich er iets van aan: wie de datum
   * intypt krijgt hem gewoon door, zodat het scherm eromheen zelf kan zeggen wat er aan de hand is. Een
   * dichte dag is namelijk geen typefout, en "Vul een datum in als dd-mm-jjjj" zou hier liegen.
   */
  const dagIsDicht = (datum: Date) => dagDicht?.(isoVanDatum(datum)) === true;
  const nietTeKiezen = (datum: Date) => buitenGrenzen(datum) || dagIsDicht(datum);

  /** Alleen doorgeven; wat er in het veld staat blijft staan zoals het getypt is. */
  const zend = (datum: Date | undefined) => onChange?.(wijziging(isoVanDatum(datum), invoer.current));

  const meld = (datum: Date | undefined) => {
    setOngeldig(false);
    setGetypt(getoond(datum));
    if (datum) setMaand(datum);
    zend(datum);
  };

  /**
   * Wat er getypt wordt meteen doorgeven zodra het een hele datum is. Het scherm eromheen rekent vaak
   * mee — een wizard die op Volgende wacht, een prijs die opnieuw wordt berekend — en dat hoort niet te
   * wachten tot je het veld verlaat. Een half jaartal (14-05-20) telt niet mee: dat zou 2020 melden
   * terwijl je 2026 aan het typen bent.
   */
  const tijdensTypen = (tekst: string) => {
    if (tekst.trim() === "") {
      if (value) zend(undefined);
      return;
    }
    const jaar = tekst.trim().split(/[^\d]+/).filter(Boolean);
    const heelJaartal = jaar.length === 3 ? jaar[0].length === 4 || jaar[2].length === 4 : tekst.replace(/[^\d]/g, "").length === 8;
    if (!heelJaartal) return;
    const datum = leesGetypteDatum(tekst);
    if (datum && !buitenGrenzen(datum) && isoVanDatum(datum) !== (value ?? "")) {
      setMaand(datum);
      zend(datum);
    }
  };

  const kies = (datum: Date | undefined) => {
    if (datum && nietTeKiezen(datum)) return;
    meld(datum);
    if (datum) {
      setOpen(false);
      invoer.current?.focus();
    }
  };

  /** Wat er getypt staat vastleggen; kan het niet, dan blijft het staan met een melding eronder. */
  const legVast = () => {
    if (getypt.trim() === "") {
      if (value) meld(undefined);
      setOngeldig(false);
      return;
    }
    const datum = leesGetypteDatum(getypt);
    if (!datum || buitenGrenzen(datum)) {
      setOngeldig(true);
      return;
    }
    meld(datum);
  };

  const toetsInVeld = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      legVast();
      setOpen(false);
    }
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const melding = error ?? (ongeldig ? woorden.ongeldig : null);
  const kalender = open && !disabled && !readOnly;

  const veld = (
    <div className="fui-datum" ref={wortel}>
      <input
        {...rest}
        ref={invoer}
        id={veldId}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        className={[className ?? "fui-field__input", "fui-datum__invoer"].join(" ")}
        value={getypt}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={melding ? true : undefined}
        aria-describedby={
          melding ? [rest["aria-describedby"], meldingId].filter(Boolean).join(" ") : rest["aria-describedby"]
        }
        aria-haspopup="dialog"
        aria-expanded={kalender}
        onChange={(e) => {
          setGetypt(e.target.value);
          setOngeldig(false);
          tijdensTypen(e.target.value);
        }}
        onKeyDown={toetsInVeld}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          legVast();
          rest.onBlur?.(e);
        }}
      />
      <button
        type="button"
        className="fui-datum__knop"
        aria-label={woorden.kalender}
        disabled={disabled || readOnly}
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setOpen((aan) => !aan);
          invoer.current?.focus();
        }}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
          <path
            d="M7 3v3M17 3v3M4.5 9h15M6.5 5h11A2.5 2.5 0 0 1 20 7.5v11A2.5 2.5 0 0 1 17.5 21h-11A2.5 2.5 0 0 1 4 18.5v-11A2.5 2.5 0 0 1 6.5 5Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {kalender &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fui-datum__popup" style={plek} role="dialog" aria-label={woorden.kalender} ref={popup}>
            <DayPicker
              animate
              mode="single"
              locale={taal.startsWith("en") ? enGB : nl}
              weekStartsOn={1}
              showOutsideDays
              selected={gekozen}
              month={maand}
              onMonthChange={setMaand}
              onSelect={kies}
              startMonth={grens.vanaf ? new Date(grens.vanaf.getFullYear(), grens.vanaf.getMonth()) : undefined}
              endMonth={grens.tot ? new Date(grens.tot.getFullYear(), grens.tot.getMonth()) : undefined}
              disabled={[
                ...(grens.vanaf ? [{ before: grens.vanaf }] : []),
                ...(grens.tot ? [{ after: grens.tot }] : []),
                ...(dagDicht ? [dagIsDicht] : []),
              ]}
              classNames={{
                root: `${standaardKlassen.root} fui-datum__kalender`,
                month_caption: `${standaardKlassen.month_caption} fui-datum__kop`,
                caption_label: `${standaardKlassen.caption_label} fui-datum__maand`,
                nav: `${standaardKlassen.nav} fui-datum__nav`,
                button_previous: `${standaardKlassen.button_previous} fui-datum__navknop`,
                button_next: `${standaardKlassen.button_next} fui-datum__navknop`,
                chevron: `${standaardKlassen.chevron} fui-datum__pijl`,
                weekday: `${standaardKlassen.weekday} fui-datum__weekdag`,
                day: `${standaardKlassen.day} fui-datum__dag`,
                day_button: `${standaardKlassen.day_button} fui-datum__dagknop`,
                selected: `${standaardKlassen.selected} fui-datum__gekozen`,
                today: `${standaardKlassen.today} fui-datum__vandaag`,
                outside: `${standaardKlassen.outside} fui-datum__buiten`,
                disabled: `${standaardKlassen.disabled} fui-datum__uit`,
              }}
            />
            {showFooterActions && (
              <div className="fui-datum__voet">
                <button
                  type="button"
                  className="fui-datum__voetknop"
                  disabled={nietTeKiezen(new Date())}
                  onClick={() => kies(new Date())}
                >
                  {woorden.vandaag}
                </button>
                <button
                  type="button"
                  className="fui-datum__voetknop"
                  onClick={() => {
                    meld(undefined);
                    setOpen(false);
                    invoer.current?.focus();
                  }}
                >
                  {woorden.wissen}
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );

  if (!label) {
    return (
      <>
        {veld}
        {melding && (
          <span className="fui-field__error" id={meldingId}>
            {melding}
          </span>
        )}
      </>
    );
  }

  return (
    <div className="fui-field">
      <label className="fui-field__label" htmlFor={veldId}>
        {label}
      </label>
      {veld}
      {melding && (
        <span className="fui-field__error" id={meldingId}>
          {melding}
        </span>
      )}
    </div>
  );
}
